import Api from '@sendsprint/api-types';
import { UserAxiosConfig } from '@src/config';
import { CONFIG_NO_ERROR_TOAST } from '@src/constants';
import ClientApi from '@src/types/client';
import { getFullName } from '@src/utils';
import { getRecipientTypeFromMapping } from '@src/utils/recipient-type';
import { localeCompare } from '@src/utils/sort';
import { AxiosResponse } from 'axios';
import {
  decorateWithAppIdAndKeyAndEmailAndHash,
  getApiEndpointResponse,
  RequestConfig
} from './api-service';

interface ExtendedRecipientI extends Api.Model.Recipient {
  DateOfBirth?: null | string;
}

const formatPaymentTypeInfo = (
  paymentTypes: Api.Model.PaymentTypeInfo[]
): ClientApi.General.PaymentTypeInfo[] =>
  paymentTypes.map(({ Id, Country, PaymentType, PaymentName, Status }) => ({
    id: Id,
    country: Country,
    paymentName: PaymentName,
    paymentType: PaymentType,
    status: Status,
    recipientType: getRecipientTypeFromMapping({
      country: Country,
      paymentType: PaymentType
    }) as ClientApi.General.RecipientType
  }));

const formatRecipient = (r: ExtendedRecipientI): ClientApi.General.Recipient => ({
  id: r.Id,
  ownerId: r.OwnerId,
  type: getRecipientTypeFromMapping({
    country: r.Country,
    paymentType: r.PaymentType
  }) as ClientApi.General.RecipientType,
  firstName: r.FirstName,
  lastName: r.LastName,
  middleName: r.MiddleName,
  fullName: getFullName({
    firstName: r.FirstName,
    middleName: r.MiddleName,
    lastName: r.LastName
  }),
  bankCode: r.BankCode,
  branchCode: r.BranchCode,
  accountNumber: r.AccountNumber,
  routingNumber: r.RoutingNumber,
  address: r.Address,
  email: r.Email,
  phoneNumber: r.Phone,
  paymentOperator: r.PaymentOperator,
  country: r.Country,
  paymentType: r.PaymentType,
  status: r.Status,
  dateOfBirth: r.DateOfBirth
});

export const formatRecipients = (data: Api.Model.Recipient[]): ClientApi.General.Recipient[] => {
  return data.map(formatRecipient).sort((a, b) => localeCompare(a.fullName, b.fullName));
};

/**
 * ===================
 * ENDPOINTS
 * ===================
 */

export const getPaymentTypesByCountryService = async (
  payload: ClientApi.Recipients.GetPaymentTypesByCountry.Request,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  config?: RequestConfig<ClientApi.Recipients.GetPaymentTypesByCountry.Request>
): Promise<ClientApi.General.PaymentTypeInfo[]> => {
  const serializedData: Api.Recipient.ListPaymentTypesByCountry.Request =
    decorateWithAppIdAndKeyAndEmailAndHash({ Country: payload.country });

  return UserAxiosConfig.post<
    Api.Recipient.ListPaymentTypesByCountry.Response,
    AxiosResponse<Api.Recipient.ListPaymentTypesByCountry.Response>
  >('/account/loadpaymenttypesbycountry', serializedData, CONFIG_NO_ERROR_TOAST)
    .then(getApiEndpointResponse)
    .then(({ Data }) => formatPaymentTypeInfo(Data || []));
};

export const addRecipientService = async (
  payload: ClientApi.Recipients.AddRecipient.Payload,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config?: RequestConfig<ClientApi.Recipients.AddRecipient.Payload>
): Promise<ClientApi.General.Recipient | null> => {
  const serializedData: ClientApi.Recipients.AddRecipient.Request =
    decorateWithAppIdAndKeyAndEmailAndHash({
      FirstName: payload.firstName.trim(),
      MiddleName: (payload.middleName || '').trim(),
      LastName: payload.lastName.trim(),

      AccountNumber: (payload.accountNumber.toString() || '').trim(),
      BankCode: payload.bankCode || '',
      BranchCode: payload.branchCode || '',
      RoutingNumber: (payload.routingNumber || '').trim(),

      Address: payload.address.trim(),

      RecipientEmail: (payload.email || '').trim(),
      Phone: (payload.phoneNumber || '').replace(/\D/g, ''), // strip non-digit characters
      PaymentOperator: (payload.paymentOperator || '').trim(),

      Country: payload.country as Api.Model.CountryInitials,
      PaymentType: payload.paymentType as Api.Model.PaymentType,
      Status: payload.saveDetails ? Api.Model.Status.Saved : Api.Model.Status.Temporary,
      DateOfBirth: payload.dateOfBirth
    });

  return UserAxiosConfig.post<
    Api.Recipient.AddRecipient.Response,
    AxiosResponse<Api.Recipient.AddRecipient.Response>
  >('/account/insertrecipient', serializedData, config)
    .then(getApiEndpointResponse)
    .then(({ Data }) => {
      if (Data === null) {
        return Data;
      }
      const [recipient] = formatRecipients(Data);
      return recipient;
    });
};
